import { useState } from 'react'
import { AlertTriangle, Loader2, MessageCircle, Send } from 'lucide-react'
import type { AiChatMessage } from '../aiConfigure'

interface AiConfigureChatPanelProps {
  messages: AiChatMessage[]
  sending: boolean
  error: string | null
  onSend: (text: string) => void
}

// Presentational only — AccountSetupStage owns the actual chat request/action
// handling (mirrors how AiSuggestionBanner is a dumb rationale/confidence card
// and every stage owns its own apply/dismiss wiring).
export function AiConfigureChatPanel({ messages, sending, error, onSend }: AiConfigureChatPanelProps) {
  const [input, setInput] = useState('')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const text = input.trim()
    if (!text || sending) return
    onSend(text)
    setInput('')
  }

  return (
    <div className="rounded-[10px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <div className="px-3 py-2 flex items-center gap-1.5" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
        <MessageCircle size={11} className="text-[#4f46e5]" />
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#475569]">Ask about these suggestions</p>
      </div>

      {messages.length > 0 && (
        <div className="max-h-64 overflow-y-auto p-3 space-y-2">
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div
                className="max-w-[85%] rounded-[10px] px-2.5 py-1.5 text-[11px] leading-snug whitespace-pre-wrap"
                style={m.role === 'user'
                  ? { background: '#4f46e5', color: '#ffffff' }
                  : { background: '#f1f5f9', color: '#1e293b' }
                }
              >
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="rounded-[10px] px-2.5 py-1.5 text-[11px] flex items-center gap-1.5" style={{ background: '#f1f5f9', color: '#475569' }}>
                <Loader2 size={11} className="animate-spin" /> Thinking…
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="px-3 pb-1.5 text-[10px] text-red-700 flex items-center gap-1.5">
          <AlertTriangle size={11} className="shrink-0" /> {error}
        </p>
      )}

      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-1.5 p-2"
        style={messages.length > 0 ? { borderTop: '1px solid #e5e7eb' } : undefined}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="e.g. why did you include Duplicate Detection?"
          disabled={sending}
          className="flex-1 text-[11px] px-2.5 py-1.5 rounded-[8px] outline-none disabled:opacity-60"
          style={{ border: '1px solid #e5e7eb', background: '#ffffff', color: '#1e293b' }}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="w-7 h-7 rounded-[8px] flex items-center justify-center shrink-0 text-white disabled:opacity-40 transition-opacity"
          style={{ background: '#4f46e5' }}
          aria-label="Send"
        >
          <Send size={12} />
        </button>
      </form>
    </div>
  )
}
